import PropTypes from 'prop-types';
import { twJoin } from 'tailwind-merge';

import Price from 'shopper/components/Price/V2';
import Text from 'shopper/components/Text';

import ErrorBoundary from 'components/ErrorBoundary';
import Image from 'components/Image';
import StopWatch from 'components/StopWatch';

import useCountdown from 'hooks/useCountdown';
import useFlashOffer from 'hooks/useFlashOffer';

import { getOfferImageLink, toOfferImageAltText } from 'lib/image';
import { toFormattedOfferOldPrice } from 'lib/offer';
import { toFormattedOfferPrice } from 'lib/offerPage';

import Tag from '../Tag';
import Footer from './Footer';
import Store from './Store';

import OFFER from 'constants/offer';

const CardContent = ({
  authors = [],
  cardTitleAs = 'span',
  isOffersShowcaseCard = false,
  offerComments,
  offerImagePriority = false,
  offerLikes,
  offerOldPrice = null,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerPublished,
  offerStatusName,
  offerTags = [],
  offerTitle,
  storeDomain,
  userId,
  userName,
  userPhoto,
  onImageClickHandler,
  isOfferExclusiveForApp,
  isOfferExclusiveForLoggedUsers,
  offerUserVisibility,
  onTitleClickHandler,
  gaEventCategory,
  offerId,
}) => {
  const flashOffer = useFlashOffer(offerId);
  const countdown = useCountdown(flashOffer?.targetDate || new Date(0));
  const showStopWatch = Boolean(flashOffer);

  return (
    <>
      <div className="relative flex size-full flex-row gap-5 lg:flex-col">
        <Store className="hidden pb-4 lg:flex" storeDomain={storeDomain} />
        <div className="relative flex justify-center">
          <Image
            alt={toOfferImageAltText(offerTitle)}
            className={twJoin(
              'flex size-[120px] self-center rounded-4 md:size-[200px]',
              offerStatusName === OFFER.STATUSES.FINISHED && 'opacity-30'
            )}
            grayscale={offerStatusName === OFFER.STATUSES.FINISHED}
            height={120}
            loading={offerImagePriority ? null : 'lazy'}
            priority={offerImagePriority}
            sizes="(max-width: 768px) 120px, 200px"
            src={getOfferImageLink(offerPhoto, 120)}
            srcSet={`
                  ${getOfferImageLink(offerPhoto, 180)} 180w,
                  ${getOfferImageLink(offerPhoto, 300)} 300w,
                `}
            width={120}
            onClick={onImageClickHandler}
          />
          {(offerTags.length > 0 ||
            isOfferExclusiveForApp ||
            isOfferExclusiveForLoggedUsers) && (
            <div className="absolute -bottom-1 left-0 flex">
              <Tag
                className="px-2 py-1"
                offerTags={offerTags}
                offerUserVisibility={offerUserVisibility}
              />
            </div>
          )}
        </div>
        <div
          className={twJoin(
            'flex size-full flex-col pb-4 lg:ml-0',
            !isOffersShowcaseCard &&
              'justify-between border-b border-neutral-high-300 dark:border-neutral-high-400'
          )}
        >
          <Store className="lg:hidden" storeDomain={storeDomain} />
          {showStopWatch && (
            <div className="mt-2">
              <ErrorBoundary>
                <StopWatch
                  hours={countdown.hours}
                  minutes={countdown.minutes}
                  phase={flashOffer.phase}
                  seconds={countdown.seconds}
                />
              </ErrorBoundary>
            </div>
          )}
          <Text
            as={cardTitleAs}
            className={twJoin(
              'mt-3 min-h-12 w-full overflow-hidden text-ellipsis break-words',
              isOffersShowcaseCard
                ? 'line-clamp-3 md:line-clamp-2'
                : 'line-clamp-2'
            )}
            onClick={onTitleClickHandler}
          >
            {offerTitle}
          </Text>
          {!isOffersShowcaseCard && (
            <Price
              className="mt-2"
              primaryInfo={toFormattedOfferPrice(offerPrice, offerPriceType)}
              secondaryInfo={toFormattedOfferOldPrice(
                offerOldPrice,
                offerPriceType
              )}
            />
          )}
        </div>
      </div>
      {!isOffersShowcaseCard && (
        <Footer
          authors={authors}
          gaEventCategory={gaEventCategory}
          likesCount={offerLikes}
          offerComments={offerComments}
          offerId={offerId}
          offerPublished={offerPublished}
          userId={userId}
          userName={userName}
          userPhoto={userPhoto}
        />
      )}
    </>
  );
};

CardContent.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      typeName: PropTypes.string.isRequired,
      specialityName: PropTypes.string,
      level: PropTypes.number.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
  cardTitleAs: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  isOfferExclusiveForLoggedUsers: PropTypes.bool,
  isOfferExclusiveForApp: PropTypes.bool,
  isOffersShowcaseCard: PropTypes.bool,
  offerComments: PropTypes.number,
  offerId: PropTypes.number.isRequired,
  offerImagePriority: PropTypes.bool,
  offerLikes: PropTypes.number,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerPublished: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string,
  offerStatusName: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
  offerTitle: PropTypes.string.isRequired,
  storeDomain: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string,
  onImageClickHandler: PropTypes.func,
  onTitleClickHandler: PropTypes.func,
};

export default CardContent;
